import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle, CheckCircle2, CopyPlus,
  KeyRound, Layers3, Loader2, Network, ShieldCheck, ShieldX, UserPlus,
  UserRoundCog, UsersRound, XCircle
} from 'lucide-react'
import { api } from '@/lib/api'
import type { CapabilityAdministration, CapabilityPolicyChange, CapabilityChange } from '@/lib/api'
import { cn } from '@/lib/utils'

type EditorSection = 'assignments' | 'delegation' | 'capabilities'

export function CapabilityPolicyEditor({ principalId, onChanged }: { principalId?: string; onChanged?: () => void }) {
  const [data,setData]=useState<CapabilityAdministration|null>(null)
  const [groupId,setGroupId]=useState('')
  const [groupName,setGroupName]=useState('')
  const [managers,setManagers]=useState<Array<{user_id:string;display_name?:string;email?:string}>>([])
  const [managerId,setManagerId]=useState('')
  const [scopeCapability,setScopeCapability]=useState('')
  const [reason,setReason]=useState('')
  const [error,setError]=useState('')
  const [busy,setBusy]=useState(false)
  const [revision,setRevision]=useState(0)
  const [pending,setPending]=useState<{change:CapabilityChange;token:string}|null>(null)
  const [section,setSection]=useState<EditorSection>('capabilities')
  const [query,setQuery]=useState('')

  useEffect(()=>{
    let active=true
    api.capabilityAdministration(principalId).then(result=>{
      if(active){setData(result);setPending(null);setError('')}
    }).catch(()=>{
      if(active){setData(null);setPending(null);setError('Administration information is unavailable.')}
    })
    return ()=>{active=false}
  },[principalId,revision])

  useEffect(()=>{
    let active=true
    if(principalId&&data?.can_manage_groups){
      api.listStaff().then(result=>{
        if(active&&Array.isArray(result))setManagers(result.flatMap(u=>{
          if(!u || typeof u!=='object' || !('user_id' in u) || typeof u.user_id!=='string' || u.user_id===principalId)return []
          return [{user_id:u.user_id,
            display_name:'display_name' in u && typeof u.display_name==='string'?u.display_name:undefined,
            email:'email' in u && typeof u.email==='string'?u.email:undefined}]
        }))
      }).catch(()=>{if(active)setManagers([])})
    }
    return ()=>{active=false}
  },[principalId,data?.can_manage_groups])

  async function submit(change:CapabilityChange,token?:string) {
    setBusy(true);setError('')
    try {
      const result=await api.changeCapabilityPolicy(change,token)
      if(result.pending && result.approval_token) {
        setPending({change,token:result.approval_token})
      } else if(result.success===true) {
        setPending(null);setRevision(v=>v+1);onChanged?.();window.dispatchEvent(new Event('ops-policy-change'))
      } else {
        setError('The change was not confirmed. Refresh access information before retrying.')
      }
    } catch {
      setError('The change could not be confirmed. Check approvals, Worker Lock and current policy, then refresh.')
    } finally {
      setBusy(false)
    }
  }

  const group=data?.groups.find(g=>g.id===groupId)
  const readOnly=principalId
    ? !!data?.assignments.some(a=>data.groups.some(g=>g.id===a.group_id&&g.read_only===1))
    : !!group?.read_only
  const editable=data?.can_mutate===true && (!!principalId || (group?.kind==='custom' && group.active===1))

  const visibleDefinitions=useMemo(()=>{
    if(!data)return []
    const q=query.trim().toLowerCase()
    return data.definitions
      .filter(c=>!readOnly||c.action_class==='read')
      .filter(c=>!q || c.display_name.toLowerCase().includes(q) || c.id.toLowerCase().includes(q) || c.domain.toLowerCase().includes(q))
  },[data,readOnly,query])

  if(!data || data.principal_id!==(principalId||null))return null

  function setPolicy(capabilityId:string,desired:CapabilityPolicyChange['desired']) {
    if(!editable || !reason.trim() || busy || pending)return
    const common={request_id:crypto.randomUUID(),capability_id:capabilityId,desired,reason:reason.trim()}
    if(principalId) {
      const existing=data.overrides.filter(o=>o.capability_id===capabilityId)
      if(existing.length>1){
        setError('This capability has scheduled changes. Resolve those before editing.')
        return
      }
      void submit({...common,operation:'principal_override',target_id:principalId,expected_override_id:existing[0]?.id??null})
    } else if(group) {
      const existing=data.policies.find(p=>p.group_id===group.id&&p.capability_id===capabilityId)
      void submit({...common,operation:'group_capability',target_id:group.id,expected_group_granted:existing?.granted??null})
    }
  }

  const sectionButton=(id:EditorSection,label:string,icon:typeof ShieldCheck,count?:number)=>{
    const Icon=icon
    return (
      <button
        key={id}
        onClick={()=>setSection(id)}
        className={cn('cap-policy-tab',section===id&&'cap-policy-tab-active')}
      >
        <Icon className="h-4 w-4" />
        <span>{label}</span>
        {typeof count==='number'&&<em>{count}</em>}
      </button>
    )
  }

  return (
    <section className="cap-policy-editor">
      <div className="cap-policy-head">
        <div>
          <span className="cap-centre-eyebrow">{principalId?'Individual adjustments':'Role policy'}</span>
          <h3>{principalId?'Manage this person’s access':'Manage role capability profiles'}</h3>
          <p>
            Every change is governed. A reason is required, and approval/Worker Lock rules remain separate.
          </p>
        </div>
        <span className={cn('cap-policy-ready',data.can_mutate&&'cap-policy-ready-live')}>
          {data.can_mutate?<><CheckCircle2 className="h-3.5 w-3.5"/>Changes available</>:<><AlertTriangle className="h-3.5 w-3.5"/>Read only</>}
        </span>
      </div>

      {!data.can_mutate&&(
        <div className="cap-centre-alert cap-centre-alert-warn">
          Capability policy is visible, but mutations remain unavailable until the platform is ready.
        </div>
      )}

      {data.can_manage_groups&&(
        <label className="cap-field">
          <span>{principalId?'Role / access profile':'Role profile'}</span>
          <select value={groupId} disabled={busy||!!pending} onChange={e=>setGroupId(e.target.value)}>
            <option value="">{principalId?'Choose a role or access profile':'Select a role profile'}</option>
            {data.groups.map(g=><option key={g.id} value={g.id}>{g.name}{g.kind==='system_default'?' — default role':''}</option>)}
          </select>
        </label>
      )}

      {data.can_mutate&&(
        <label className="cap-field">
          <span>Reason for change</span>
          <textarea
            maxLength={2000}
            rows={2}
            value={reason}
            disabled={busy||!!pending}
            onChange={e=>setReason(e.target.value)}
            placeholder="Why is this access changing?"
          />
          <small>{reason.length}/2000</small>
        </label>
      )}

      {error&&(
        <div role="alert" className="cap-centre-alert cap-centre-alert-error">
          <XCircle className="h-4 w-4"/>{error}
        </div>
      )}

      {pending&&(
        <div role="status" className="cap-centre-alert cap-centre-alert-warn">
          <div className="min-w-0">
            <strong>Approval pending</strong>
            <p>Approve the exact request in Approval Centre, then retry the approved change here.</p>
          </div>
          <button className="cap-action-button" disabled={busy} onClick={()=>void submit(pending.change,pending.token)}>
            {busy?<Loader2 className="h-4 w-4 animate-spin"/>:<KeyRound className="h-4 w-4"/>}
            Retry approved change
          </button>
        </div>
      )}

      <div className="cap-policy-tabs">
        {sectionButton('capabilities','Capabilities',ShieldCheck,visibleDefinitions.length)}
        {principalId&&sectionButton('assignments','Assignments',UsersRound,data.assignments.length)}
        {principalId&&data.can_manage_groups&&sectionButton('delegation','Delegation',Network,data.management_scopes.length)}
      </div>

      {section==='assignments'&&principalId&&(
        <div className="cap-policy-panel">
          <div className="cap-policy-panel-head">
            <div>
              <h4>Assigned role and access profiles</h4>
              <p>The assigned role or access profile provides the person’s default capability set.</p>
            </div>
            {data.can_mutate&&data.can_manage_groups&&group&&(
              <button
                disabled={busy||!!pending||!reason.trim()||group.active!==1||data.assignments.some(a=>a.group_id===group.id)}
                className="cap-action-button cap-action-button-primary"
                onClick={()=>void submit({
                  request_id:crypto.randomUUID(),operation:'assign_group',target_id:principalId,
                  group_id:group.id,expected_assignment_id:null,reason:reason.trim()
                })}
              >
                <UserPlus className="h-4 w-4"/>Assign selected profile
              </button>
            )}
          </div>

          {data.assignments.length===0?(
            <div className="cap-empty-state">No active role or access-profile assignments.</div>
          ):(
            <div className="cap-assignment-list">
              {data.assignments.map(a=>{
                const groupInfo=data.groups.find(g=>g.id===a.group_id)
                return (
                  <div key={a.id} className="cap-assignment-row">
                    <span className="cap-assignment-icon"><Layers3 className="h-4 w-4"/></span>
                    <div className="min-w-0 flex-1">
                      <strong>{groupInfo?.name||a.group_id}</strong>
                      <span>{a.status} · effective {a.effective_from}{a.expires_at?' · expires '+a.expires_at:' · no expiry'}</span>
                    </div>
                    {data.can_mutate&&data.can_manage_groups&&(
                      <button
                        disabled={busy||!!pending||!reason.trim()}
                        className="cap-small-danger"
                        onClick={()=>void submit({
                          request_id:crypto.randomUUID(),operation:'revoke_assignment',target_id:principalId,
                          group_id:a.group_id,expected_assignment_id:a.id,reason:reason.trim()
                        })}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {section==='delegation'&&principalId&&data.can_manage_groups&&(
        <div className="cap-policy-panel">
          <div className="cap-policy-panel-head">
            <div>
              <h4>Delegated administration</h4>
              <p>Delegate may administer only the selected capability for this person. Self-escalation remains prohibited.</p>
            </div>
          </div>

          {data.can_mutate&&(
            <div className="cap-delegation-form">
              <label className="cap-field">
                <span>Delegate</span>
                <select value={managerId} onChange={e=>setManagerId(e.target.value)} disabled={busy||!!pending}>
                  <option value="">Choose delegate</option>
                  {managers.map(m=><option key={m.user_id} value={m.user_id}>{m.display_name||m.email||m.user_id}</option>)}
                </select>
              </label>
              <label className="cap-field">
                <span>Capability</span>
                <select value={scopeCapability} onChange={e=>setScopeCapability(e.target.value)} disabled={busy||!!pending}>
                  <option value="">Choose capability</option>
                  {data.definitions.map(c=><option key={c.id} value={c.id}>{c.display_name}</option>)}
                </select>
              </label>
              <button
                disabled={
                  busy||!!pending||!reason.trim()||!managerId||!scopeCapability||
                  data.management_scopes.some(s=>s.manager_id===managerId&&s.capability_id===scopeCapability)
                }
                className="cap-action-button cap-action-button-primary cap-delegation-submit"
                onClick={()=>void submit({
                  request_id:crypto.randomUUID(),operation:'grant_management_scope',target_id:principalId,
                  manager_id:managerId,capability_id:scopeCapability,
                  expected_scope_id:null,reason:reason.trim()
                })}
              >
                <Network className="h-4 w-4"/>Grant delegated administration
              </button>
            </div>
          )}

          {data.management_scopes.length===0?(
            <div className="cap-empty-state">No delegated administration scopes.</div>
          ):(
            <div className="cap-assignment-list">
              {data.management_scopes.map(scope=>(
                <div key={scope.id} className="cap-assignment-row">
                  <span className="cap-assignment-icon"><UserRoundCog className="h-4 w-4"/></span>
                  <div className="min-w-0 flex-1">
                    <strong>{managers.find(m=>m.user_id===scope.manager_id)?.display_name||scope.manager_id}</strong>
                    <span>{data.definitions.find(c=>c.id===scope.capability_id)?.display_name||scope.capability_id}</span>
                  </div>
                  {data.can_mutate&&(
                    <button
                      disabled={busy||!!pending||!reason.trim()}
                      className="cap-small-danger"
                      onClick={()=>void submit({
                        request_id:crypto.randomUUID(),operation:'revoke_management_scope',target_id:principalId,
                        manager_id:scope.manager_id,
                        capability_id:scope.capability_id,expected_scope_id:scope.id,reason:reason.trim()
                      })}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section==='capabilities'&&(
        <div className="cap-policy-panel">
          {!principalId&&group?.kind==='system_default'&&(
            <div className="cap-template-clone">
              <div>
                <strong>Protected default role</strong>
                <p>Default role profiles are protected. Create a custom access profile when a different policy is needed.</p>
              </div>
              {data.can_mutate&&group.active===1&&(
                <div className="cap-template-clone-form">
                  <input
                    value={groupName}
                    maxLength={120}
                    disabled={busy||!!pending}
                    onChange={e=>setGroupName(e.target.value)}
                    placeholder="New custom access profile name"
                  />
                  <button
                    disabled={busy||!!pending||!reason.trim()||!groupName.trim()}
                    className="cap-action-button cap-action-button-primary"
                    onClick={()=>void submit({
                      request_id:crypto.randomUUID(),operation:'clone_group',target_id:crypto.randomUUID(),
                      source_group_id:group.id,name:groupName.trim(),reason:reason.trim()
                    })}
                  >
                    <CopyPlus className="h-4 w-4"/>Create custom access profile
                  </button>
                </div>
              )}
            </div>
          )}

          <label className="cap-search mb-3">
            <ShieldCheck className="h-4 w-4"/>
            <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Filter capabilities..." />
          </label>

          {!principalId&&!group?(
            <div className="cap-empty-state">Choose a role or access profile to view its capability policy.</div>
          ):(
            <div className="cap-policy-cap-list">
              {visibleDefinitions.map(cap=>{
                const individual=data.overrides.filter(o=>o.capability_id===cap.id)
                const policy=data.policies.find(p=>p.group_id===groupId&&p.capability_id===cap.id)
                const state=principalId
                  ? (individual.length>1?'scheduled':individual[0]?.override_type??'inherit')
                  : (policy?policy.granted===1?'grant':'restrict':'inherit')
                const locked=data.locked_capabilities.includes(cap.id)
                return (
                  <div key={cap.id} className="cap-policy-cap-row">
                    <div className="cap-policy-cap-copy">
                      <span className="cap-domain-label">{cap.domain}</span>
                      <strong>{cap.display_name}</strong>
                      <small>{cap.id}</small>
                    </div>
                    <div className="cap-policy-cap-state">
                      {locked&&<span className="cap-locked-pill"><KeyRound className="h-3 w-3"/>Locked</span>}
                      <span className={cn(
                        'cap-policy-state-pill',
                        state==='grant'&&'cap-policy-state-grant',
                        state==='restrict'&&'cap-policy-state-restrict',
                        state==='scheduled'&&'cap-policy-state-scheduled'
                      )}>
                        {state==='grant'?'Granted':state==='restrict'?'Restricted':state==='scheduled'?'Scheduled':'Inherited'}
                      </span>
                    </div>
                    {editable&&!locked&&(
                      <div className="cap-policy-actions">
                        <button
                          disabled={busy||!!pending||!reason.trim()||state==='scheduled'||state==='grant'}
                          className="cap-policy-action cap-policy-action-on"
                          onClick={()=>setPolicy(cap.id,'grant')}
                        >
                          <ShieldCheck className="h-3.5 w-3.5"/>On
                        </button>
                        <button
                          disabled={busy||!!pending||!reason.trim()||state==='scheduled'||state==='restrict'}
                          className="cap-policy-action cap-policy-action-off"
                          onClick={()=>setPolicy(cap.id,'restrict')}
                        >
                          <ShieldX className="h-3.5 w-3.5"/>Off
                        </button>
                        <button
                          disabled={busy||!!pending||!reason.trim()||state==='scheduled'||state==='inherit'}
                          className="cap-policy-action"
                          onClick={()=>setPolicy(cap.id,'inherit')}
                        >
                          Inherit
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
